"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});

/**
 * Variant Selection scripts
 * ------------------------------------------------------------------------------
 *
 * Handles change events from the variant inputs in any `cart/add` forms that may
 * exist. Also updates the master select and triggers updates when the variants
 * price or image changes.
 *
 * @namespace variants
 */

class Variants {
  /**
   * Variant constructor
   *
   * @param {object} options - Settings from `product.js`
   */
  constructor(options) {
    this.container = options.container;
    this.product = options.product;
    this.singleOptionSelector = options.singleOptionSelector;
    this.originalSelectorId = options.originalSelectorId;
    this.enableHistoryState = options.enableHistoryState;
    this.currentVariant = this._getVariantFromOptions();

    const optionElements = this.container.querySelectorAll(this.singleOptionSelector);
    optionElements.forEach(element => element.addEventListener("change", this._onSelectChange.bind(this)));
  }

  /**
   * Get the currently selected options from add-to-cart form. Works with all
   * form input elements.
   *
   * @return {array} options - Values of currently selected variants
   */
  _getCurrentOptions() {
    const optionElements = this.container.querySelectorAll(this.singleOptionSelector);
    const currentOptions = Array.from(optionElements).map(element => {
      const type = element.getAttribute("type");
      const currentOption = {};

      if (type === "radio" || type === "checkbox") {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;
          return currentOption;
        } else {
          return null;
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;
        return currentOption;
      }
    }).filter(option => option !== null);

    return currentOptions;
  }

  /**
   * Find variant based on selected values.
   *
   * @param  {array} selectedValues - Values of variant inputs
   * @return {object || undefined} found - Variant object from product.variants
   */
  _getVariantFromOptions() {
    const selectedValues = this._getCurrentOptions();
    const variants = this.product.variants;
    let found = null;

    variants.forEach(variant => {
      let satisfied = true;

      selectedValues.forEach(option => {
        if (satisfied) {
          satisfied = option.value === variant[option.index];
        }
      });

      if (satisfied) {
        found = variant;
      }
    });

    return found;
  }

  /**
   * Event handler for when a variant input changes.
   */
  _onSelectChange() {
    const variant = this._getVariantFromOptions();

    const event = new CustomEvent("variantChange", {
      detail: { variant: variant }
    });
    this.container.dispatchEvent(event);

    if (!variant) {
      return;
    }

    this._updateMasterSelect(variant);
    setTimeout(() => {
      this._updateImages(variant);
      this._updatePrice(variant);
      this.currentVariant = variant;

      if (this.enableHistoryState) {
        this._updateHistoryState(variant);
      }
    }, 0);
  }

  /**
   * Trigger event when variant image changes
   *
   * @param  {object} variant - Currently selected variant
   */
  _updateImages(variant) {
    const variantImage = variant.featured_image || {};
    const currentVariantImage = this.currentVariant.featured_image || {};

    if (!variant.featured_image || variantImage.src === currentVariantImage.src) {
      return;
    }

    const event = new CustomEvent("variantImageChange", {
      detail: { variant: variant }
    });
    this.container.dispatchEvent(event);
  }

  /**
   * Trigger event when variant price changes.
   *
   * @param  {object} variant - Currently selected variant
   */
  _updatePrice(variant) {
    if (variant.price === this.currentVariant.price && variant.compare_at_price === this.currentVariant.compare_at_price) {
      return;
    }

    const event = new CustomEvent("variantPriceChange", {
      detail: { variant: variant }
    });
    this.container.dispatchEvent(event);
  }

  /**
   * Update history state for product deeplinking
   *
   * @param {object} variant - Currently selected variant
   */
  _updateHistoryState(variant) {
    if (!history.replaceState || !variant) {
      return;
    }

    const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}`;
    window.history.replaceState({ path: newurl }, "", newurl);
  }

  /**
   * Update hidden master select of variant change
   *
   * @param {object} variant - Currently selected variant
   */
  _updateMasterSelect(variant) {
    const masterSelect = this.container.querySelector(this.originalSelectorId);
    if (masterSelect) {
      masterSelect.value = variant.id;
    }
  }
}

exports.default = Variants;